import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  TablePagination,
  Skeleton,
} from '@mui/material';
import {
  Search,
  Add,
  Visibility,
  VisibilityOff,
  ChevronRight,
  ExpandMore,
  MoreVert,
  Article,
  MoreVertSharp,
  MoreHoriz,

} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SearchInput from '../../../../components/common/SearchInputField';
import ButtonMain from '../../../../components/common/ButtonMain';
import { ReactComponent as Folder } from '../../../../assets/images/categoryFolder.svg';
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as VerticalMenu } from '../../../../assets/images/vertical-menu.svg';
import { ReactComponent as Eye } from '../../../../assets/images/eye.svg';
import { ReactComponent as Hidden } from '../../../../assets/images/hidden.svg';
import AddCategoryPopup from '../../../../components/dashboardcomponents/KnowledgeBase/Category/AddCategory';
import EditCategoryPopup from '../../../../components/dashboardcomponents/KnowledgeBase/Category/EditCategory';
import DeleteCategoryPopUp from '../../../../components/dashboardcomponents/KnowledgeBase/Category/DeleteCategory';
import { useDispatch, useSelector } from 'react-redux';
import { getAllKbCategories } from '../../../../store/features/getCategoriesToKnowledgeBase';
import { updateKbCategory } from '../../../../store/features/updateCategoryToKb';
import { archiveKbArticle } from '../../../../store/features/kbActions';
import { removeKbArticleFromCategory } from '../../../../store/features/removeKbArticleFromCategory';
import { updateKbArticleStatus, updateKbArticleVisibility } from '../../../../store/features/kbBulkActions';
import NoDataFound from "../../../../components/dashboardcomponents/Traffic/NoDataFound";
import CategoryTable from '../../../../components/dashboardcomponents/KnowledgeBase/Category/CategoryTable';
import { toast } from 'react-toastify';
import GroupSelectDropdown from '../../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown';
import { useAnalyticsGroupId } from '../../../../hooks/useAnalyticsGroupId';
import { fetchWorkspaceSettings } from '../../../../store/features/workspaceSettingsApi';
import { selectBusinessSlug } from '../../../../utils/kbPublicUrl';
import { isHeaderInlinePageTitleEnabled } from '../../../../config/featureFlags';
import '../../TicketManagment/style.css';

const Categories = () => {
  const [filter, setFilter] = React.useState('all');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [expandedIds, setExpandedIds] = React.useState([]);
  const [openAddCategory, setOpenAddCategory] = React.useState(false);
  const [openEditCategory, setOpenEditCategory] = React.useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = React.useState(false);
  const { loading, error, data } = useSelector((state) => state.getAllKbCategories);
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();
  const isLoading = !selectedGroupId || loading;

  const dispatch = useDispatch();
  const [categories, setCategories] = React.useState([]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [categoryToEdit, setCategoryToEdit] = React.useState(null);
  const [categoryToDelete, setCategoryToDelete] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [activeId, setActiveId] = React.useState(null);

  const userData = useSelector((state) => state.loginToken);
  const kbRole = userData?.response?.user?.kbRole?.role;
  const businessName = useSelector((state) => state.loginToken?.response?.user?.business_name);
  const businessSlug = useSelector(selectBusinessSlug);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Hydrate local rows from Redux before paint so revisits do not flash empty state.
  useLayoutEffect(() => {
    const raw = data?.data;
    if (!Array.isArray(raw)) {
      if (!data) setCategories([]);
      return;
    }
    setCategories(
      raw.map((cat) => ({
        id: cat._id,
        title: cat.name,
        totalArticles: cat.articleCount || 0,
        visibility: cat.visibility === 'public' ? 'public' : 'internal',
        articlesList: (cat.articles || []).map((article) => ({
          id: article._id,
          _id: article._id,
          title: article.title,
          status: article.status,
          visibility: article.visibility === 'public' ? 'public' : 'internal',
          parentId: cat._id,
        })),
      }))
    );
  }, [data]);

  const getCounts = () => {
    const q = searchTerm.trim().toLowerCase();
    const matched = q
      ? categories.filter(c => c.title.toLowerCase().includes(q))
      : categories;
    const all = matched.length;
    const publicCount = matched.filter(c => c.visibility === 'public').length;
    const internalCount = matched.filter(c => c.visibility === 'internal').length;
    return { all, public: publicCount, internal: internalCount };
  };

  const counts = getCounts();

  useEffect(() => {
    if (selectedGroupId) {
      dispatch(getAllKbCategories({ group_id: selectedGroupId }));
    }
  }, [dispatch, selectedGroupId]);

  useEffect(() => {
    if (userData?.response?.user?.business_id && !businessSlug) {
      dispatch(fetchWorkspaceSettings());
    }
  }, [dispatch, userData?.response?.user?.business_id, businessSlug]);

  const handleArticleAction = async (type, articleId, categoryId) => {
    if (!selectedGroupId) return;
    if (type === 'remove') {
      if (!categoryId) {
        toast.error('Missing category for this action');
        return;
      }
      const result = await dispatch(
        removeKbArticleFromCategory({ articleId, categoryId, group_id: selectedGroupId })
      );
      if (removeKbArticleFromCategory.fulfilled.match(result)) {
        toast.success(
          result.payload?.message || 'Article removed from category successfully'
        );
        dispatch(getAllKbCategories({ group_id: selectedGroupId }));
      } else {
        toast.error(result.payload || 'Failed to remove article from category');
      }
    } else if (type === 'archive') {
      const result = await dispatch(archiveKbArticle({ articleId, group_id: selectedGroupId }));
      if (archiveKbArticle.fulfilled.match(result)) {
        toast.success(result.payload?.message || 'Article archived successfully');
        dispatch(getAllKbCategories({ group_id: selectedGroupId }));
      } else {
        toast.error(result.payload || 'Failed to archive article');
      }
    } else if (type === 'publish') {
      const result = await dispatch(updateKbArticleStatus({ articleIds: [articleId], status: 'published', group_id: selectedGroupId }));
      if (updateKbArticleStatus.fulfilled.match(result)) {
        toast.success(result.payload?.message || 'Article published successfully');
        dispatch(getAllKbCategories({ group_id: selectedGroupId }));
      } else {
        toast.error(result.payload || 'Failed to publish article');
      }
    } else if (type === 'unpublish') {
      const result = await dispatch(updateKbArticleStatus({ articleIds: [articleId], status: 'pendingReview', group_id: selectedGroupId }));
      if (updateKbArticleStatus.fulfilled.match(result)) {
        toast.success(result.payload?.message || 'Article unpublished successfully');
        dispatch(getAllKbCategories({ group_id: selectedGroupId }));
      } else {
        toast.error(result.payload || 'Failed to unpublish article');
      }
    } else if (type === 'public') {
      const result = await dispatch(updateKbArticleVisibility({ articleIds: [articleId], visibility: 'public', group_id: selectedGroupId }));
      if (updateKbArticleVisibility.fulfilled.match(result)) {
        toast.success(result.payload?.message || 'Article set to public');
        dispatch(getAllKbCategories({ group_id: selectedGroupId }));
      } else {
        toast.error(result.payload || 'Failed to update visibility');
      }
    } else if (type === 'internal') {
      const result = await dispatch(updateKbArticleVisibility({ articleIds: [articleId], visibility: 'internal', group_id: selectedGroupId }));
      if (updateKbArticleVisibility.fulfilled.match(result)) {
        toast.success(result.payload?.message || 'Article set to internal');
        dispatch(getAllKbCategories({ group_id: selectedGroupId }));
      } else {
        toast.error(result.payload || 'Failed to update visibility');
      }
    }
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      setPage(0);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePublicSelection = () => {
    if (selectedCategory && selectedCategory.visibility !== 'public') {
      setCategories(prev =>
        prev.map(c =>
          c.id === selectedCategory.id
            ? {
              ...c,
              visibility: 'public',
              articlesList: c.articlesList?.map(a => ({ ...a, visibility: 'public' })) || [],
            }
            : c
        )
      );
      // No refetch needed if visibility doesn't affect total counts
    }
    handleMenuClose();
  };

  const handleInternalSelection = () => {
    if (selectedCategory && selectedCategory.visibility !== 'internal') {
      setCategories(prev =>
        prev.map(c =>
          c.id === selectedCategory.id
            ? {
              ...c,
              visibility: 'internal',
              articlesList: c.articlesList?.map(a => ({ ...a, visibility: 'internal' })) || [],
            }
            : c
        )
      );
      // No refetch needed if visibility doesn't affect total counts
    }
    handleMenuClose();
  };

  const handleEditCategory = () => {
    setCategoryToEdit(selectedCategory);
    setOpenEditCategory(true);
    handleMenuClose();
  };

  const handleDeleteCategory = () => {
    setCategoryToDelete(selectedCategory);
    setOpenDeleteCategory(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (categoryToDelete) {
      setCategories(prev => prev.filter(c => c.id !== categoryToDelete.id));
    }
    setOpenDeleteCategory(false);
    setCategoryToDelete(null);
    setSelectedCategory(null);
  };

  const handleEditUpdate = (updatedCategory) => {
    setCategories(prev => {
      if (updatedCategory.parentId) {
        // Updating a subcategory
        return prev.map(parent => {
          if (parent.id === updatedCategory.parentId) {
            return {
              ...parent,
              // Update parent's totalArticles if subcategory's articles changed
              totalArticles: parent.totalArticles - (prev.find(p => p.id === updatedCategory.parentId)?.articlesList.find(child => child.id === updatedCategory.id)?.articles || 0) + (updatedCategory.articles || 0),
              articlesList: parent.articlesList.map(child =>
                child.id === updatedCategory.id ? { ...updatedCategory } : child
              )
            };
          }
          return parent;
        });
      } else {
        // Updating a parent category
        return prev.map(c =>
          c.id === updatedCategory.id
            ? {
              ...updatedCategory,
              // Ensure articlesList is preserved if not provided
              articlesList: c.articlesList || updatedCategory.articlesList || []
            }
            : c
        );
      }
    });
    // If count changed in edit, it's already handled above; refetch only if needed for other sync
    // dispatch(getAllKbCategories()); // Optional: remove if local update suffices
    setCategoryToEdit(null);
    setSelectedCategory(null);
  };

  const handleEditClose = () => {
    setOpenEditCategory(false);
    setCategoryToEdit(null);
  };

  const handleDeleteClose = () => {
    setOpenDeleteCategory(false);
    setCategoryToDelete(null);
  };

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handle drag end - Updated for category reordering only
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    // Only handle reordering of categories (articles are not draggable)
    const oldIndex = categories.findIndex(cat => cat.id === active.id);
    const newIndex = categories.findIndex(cat => cat.id === over.id);

    if (oldIndex !== newIndex) {
      const movedCategory = categories[oldIndex];
      const previousCategories = [...categories];
      const reorderedCategories = arrayMove(categories, oldIndex, newIndex);

      // Update UI locally first
      setCategories(reorderedCategories);

      // Update via API
      try {
        await dispatch(updateKbCategory({
          categoryId: active.id,
          name: movedCategory.title,
          visibility: movedCategory.visibility,
          order: newIndex + 1, // 1-based order
          group_id: selectedGroupId,
        })).unwrap();
      } catch (error) {
        console.error('Failed to update category order:', error);
        setCategories(previousCategories);
      }
    }

    setActiveId(null);
  };

  const filteredCategories = categories.filter((category) => {
    const matchesFilter = filter === 'all' || category.visibility.toLowerCase() === filter;
    const matchesSearch = category.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const visibleCategories = React.useMemo(() => {
    return filteredCategories.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredCategories, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isLastCategory = (index) => index === visibleCategories.length - 1;
  const getCategoryBorderBottom = (index, isExpanded) =>
    isLastCategory(index) && !isExpanded ? 'none' : '1px solid #E5E7EB';

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const renderSkeletonRows = () => (
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={`skeleton-${i}`}>
        <TableCell sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
            <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width={80} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 2 }}>
          <Skeleton variant="rectangular" width={80} height={24} />
        </TableCell>
        <TableCell sx={{ py: 2 }}>
          <Skeleton variant="circular" width={24} height={24} />
        </TableCell>
      </TableRow>
    ))
  );

  const getVisibilityDisplay = (visibility) => {
    return visibility === 'public' ? 'Public' : 'Internal';
  };

  // Get all category IDs for sortable context
  const allIds = React.useMemo(() => {
    return visibleCategories.map(cat => cat.id);
  }, [visibleCategories]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          padding: '24px',
          paddingTop: isHeaderInlinePageTitleEnabled ? 0 : '16px',
          paddingBottom: '8px',
          backgroundColor: '#fff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row">
            <p className="Ticket-text">Categories</p>
            <GroupSelectDropdown
              onSelect={setSelectedGroupId}
              value={selectedGroupId}
            />
          </div>
        )}

        <div
          style={{
            marginTop: isHeaderInlinePageTitleEnabled ? 0 : "30px",
            display: "flex",
            gap: "24px",
            position: "relative",
          }}
        >
          {Object.entries(counts).map(([key, count]) => (
            <button
              key={key}
              onClick={() => handleFilterChange(null, key)}
              style={{
                border: "none",
                background: "none",
                padding: "0 0 8px 0",
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                fontWeight: filter === key ? 500 : 400,
                color: filter === key ? "#061522" : "#405261",
                cursor: "pointer",
                position: "relative",
                borderBottom: filter === key ? "1px solid #148DFD" : "none",
              }}
            >
              {`${key.charAt(0).toUpperCase() + key.slice(1)} (${count})`}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: '30px', display: 'flex', marginTop: '24px', justifyContent: 'space-between' }}>
          <SearchInput
            title=""
            placeholder="Search categories"
            className="SearchInputField-categorysection"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {(kbRole === 'editor' || kbRole === 'contributor') && (
            <ButtonMain
              showIcon
              className="inviteButton-category"
              onClick={() => setOpenAddCategory(true)}
              title="New Category"
            />
          )}
        </div>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <CategoryTable
            loading={isLoading}
            visibleCategories={visibleCategories}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
            handleMenuOpen={handleMenuOpen}
            getVisibilityDisplay={getVisibilityDisplay}
            kbRole={kbRole}
            allIds={allIds}
            isLastCategory={isLastCategory}
            getCategoryBorderBottom={getCategoryBorderBottom}
            renderSkeletonRows={renderSkeletonRows}
            businessName={businessName}
            onArticleAction={handleArticleAction}
          />

          {filteredCategories.length > rowsPerPage && (
            <Box
              sx={{
                borderTop: "1px solid #E5E9EC",
                padding: "16px 0px",
                flexShrink: 0,
              }}
            >
              <TablePagination
                component="div"
                count={filteredCategories.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) => (
                  <Typography
                    sx={{
                      color: "#405261",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "18px",
                    }}
                  >
                    Categories {from}-{to}
                  </Typography>
                )}
                ActionsComponent={(props) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      marginLeft: "auto",
                    }}
                  >
                    <Button
                      onClick={(e) => props.onPageChange(e, props.page - 1)}
                      disabled={props.page === 0}
                      sx={{
                        width: "128px",
                        height: "40px",
                        flexShrink: 0,
                        borderRadius: "8px",
                        border: "1px solid #D5DCE1",
                        background: "#FFF",
                        boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                        color: "#405261",
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "18px",
                        textTransform: "none",
                        "&:not(.Mui-disabled):hover": {
                          backgroundColor: "#FFFFFF",
                          borderColor: "#C5CED6",
                        },
                        "&.Mui-disabled": {
                          color: "#93A1B0",
                          border: "1px solid #C5CED6",
                          bgcolor: "#F9FAFA",
                        },
                      }}
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={(e) => props.onPageChange(e, props.page + 1)}
                      disabled={
                        props.page >= Math.ceil(props.count / rowsPerPage) - 1
                      }
                      sx={{
                        width: "88px",
                        height: "40px",
                        flexShrink: 0,
                        borderRadius: "8px",
                        border: "1px solid #D5DCE1",
                        boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                        color: "#405261",
                        backgroundColor: "#FFFFFF",
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "18px",
                        textTransform: "none",
                        "&:not(.Mui-disabled):hover": {
                          backgroundColor: "#FFFFFF",
                          borderColor: "#C5CED6",
                        },
                        "&.Mui-disabled": {
                          color: "#93A1B0",
                          border: "1px solid #C5CED6",
                          bgcolor: "#F9FAFA",
                        },
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                )}
                sx={{
                  ".MuiTablePagination-toolbar": {
                    padding: 0,
                    minHeight: "unset",
                    display: "flex",
                    justifyContent: "space-between",
                  },
                  ".MuiTablePagination-spacer": {
                    display: "none",
                  },
                  ".MuiTablePagination-selectLabel": {
                    display: "none",
                  },
                  ".MuiTablePagination-select": {
                    display: "none",
                  },
                  ".MuiTablePagination-selectIcon": {
                    display: "none",
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            elevation: 0,
            sx: {
              filter: "none",
              color: '#405261',
              mt: '2px',
              width: 184,
              paddingX: "8px",
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
              "& .MuiMenuItem-root": {
                px: "8px",
                py: 1,
                fontSize: '14px',
                fontFamily: 'Inter',
                color: '#405261',
                "&:hover": { color: "#061522", borderRadius: "8px", background: "#F9FAFA !important" },
              },
            },
          }}
        >
          <MenuItem
            disabled={kbRole !== 'editor' && kbRole !== 'contributor'}
            sx={{
              ...(kbRole === 'editor' || kbRole === 'contributor' && {
                color: '#93A1B0',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                "&:hover": {
                  color: '#93A1B0',
                  background: "#F9FAFA !important",
                  borderRadius: '8px'
                },
              }),
              ...(kbRole !== 'editor' && kbRole !== 'contributor' && {
                color: 'rgba(64, 82, 97, 0.4)',
                "&:hover": {
                  color: 'rgba(64, 82, 97, 0.4)',
                  background: 'transparent !important',
                  borderRadius: '8px'
                },
              }),
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (kbRole === 'editor' || kbRole === 'contributor') {
                handleEditCategory();
              }
            }}
          >
            Edit Category
          </MenuItem>
          <MenuItem
            disabled={kbRole !== 'editor'}
            sx={{
              ...(kbRole === 'editor' && {
                color: '#FF4D4F !important',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                "&:hover": {
                  color: '#FF4D4F !important',
                  background: "#F9FAFA !important",
                  borderRadius: '8px'
                },
              }),
              ...(kbRole !== 'editor' && {
                color: 'rgba(64, 82, 97, 0.4)',
                "&:hover": {
                  color: 'rgba(64, 82, 97, 0.4)',
                  background: 'transparent !important',
                  borderRadius: '8px'
                },
              }),
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (kbRole === 'editor') {
                handleDeleteCategory();
              }
            }}
          >
            Delete
          </MenuItem>
        </Menu>

        <AddCategoryPopup
          open={openAddCategory}
          availableCategories={categories}
          onClose={() => setOpenAddCategory(false)}
          groupId={selectedGroupId}
        />
        <EditCategoryPopup
          open={openEditCategory}
          category={categoryToEdit}
          onClose={handleEditClose}
          onUpdate={handleEditUpdate}
          groupId={selectedGroupId}
        />
        <DeleteCategoryPopUp
          open={openDeleteCategory}
          category={categoryToDelete}
          onClose={handleDeleteClose}
          onDelete={handleDeleteConfirm}
          groupId={selectedGroupId}
        />
      </Box>

      {/* DragOverlay to show the dragged item */}
      <DragOverlay dropAnimation={null}>
        {activeId ? (
          (() => {
            // Find the active item being dragged
            const activeItem = visibleCategories
              .flatMap(cat => cat.articlesList || [])
              .find(article => article.id === activeId);

            if (activeItem) {
              return (
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    minWidth: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'grabbing',
                  }}
                >
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'transparent', '& svg': { color: '#405261' } }}>
                    <VerticalMenu />
                  </Avatar>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'transparent', '& svg': { color: '#9CA3AF' } }}>
                    <Folder width={24} height={24} />
                  </Avatar>
                  <Typography
                    sx={{
                      color: '#061522',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 14,
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {activeItem.title}
                  </Typography>
                  {activeItem.articles > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      ({activeItem.articles} Articles)
                    </Typography>
                  )}
                </Box>
              );
            }
            return null;
          })()
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default Categories;